import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { populateFormFields, deriveSourceFields } from "@/lib/formMapping";
import { generateFilledPdf } from "@/lib/pdfFormFiller";
import type { Employee } from "@shared/schemas";
import { AlertTriangle, ArrowLeft, ArrowRight, Check, FileText, Loader2 } from "lucide-react";
import {
  SigningDocViewer,
  type SigningDocViewerHandle,
  type SigningField,
} from "./SigningDocViewer";
import { isFormNotReady } from "@/lib/formReadiness";
import { applyFullSsns, fetchFullSsns } from "@/lib/ssnReveal";

interface FieldMapping {
  id: number;
  formFieldId: number;
  sourcePath: string;
  sourceType: string;
  fieldLabel?: string;
  fieldName?: string;
  matchValue?: string | null;
  matchPlanId?: number | null;
  transformationType?: string | null;
  transformationConfig?: any;
  conditionalLogic?: any;
}

interface EnrollmentSigningViewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  signatureName: string;
  signatureData: string;
  selectedPlanIds: number[];
  employee: Employee | null;
  enrollmentContext: any;
  onConfirm: (formData: any) => Promise<void>;
  /** Return to the elections/summary step to review or change selections. */
  onBack?: () => void;
  isLoading?: boolean;
  /** Signed signature-field keys restored from a saved draft, to resume signing. */
  initialSignedKeys?: string[];
  /** Reports the current signed-key set so the parent can autosave signing progress. */
  onSignedKeysChange?: (keys: string[]) => void;
}

const isSignatureType = (f: any) =>
  String(f?.fieldType ?? "").toLowerCase() === "signature";

// A field can only be shown/placed on the document if it has a positioned
// (relative) coordinate box. Carrier forms that were data-mapped but never
// field-placed have none, so we can't draw a "Sign" tab for them.
const hasPlacedCoords = (f: any): boolean => {
  const c = f?.fieldCoordinates;
  return (
    !!c &&
    c.unit === "relative" &&
    typeof c.x === "number" &&
    typeof c.y === "number" &&
    typeof c.width === "number" &&
    typeof c.height === "number"
  );
};

const todayLabel = () => new Date().toLocaleDateString("en-US");

export function EnrollmentSigningView({
  open,
  onOpenChange,
  signatureName,
  signatureData,
  selectedPlanIds,
  employee: maskedEmployee,
  enrollmentContext: maskedEnrollmentContext,
  onConfirm,
  onBack,
  isLoading = false,
  initialSignedKeys,
  onSignedKeysChange,
}: EnrollmentSigningViewProps) {
  const { user } = useAuth();
  // Employee/dependent rows from the API carry a masked SSN; carrier forms need
  // the real one, so fetch it (audited) and merge before mapping form fields.
  const { data: fullSsns, isLoading: ssnLoading } = useQuery({
    queryKey: ["employee-full-ssn", maskedEmployee?.id],
    enabled: open && !!maskedEmployee?.id,
    staleTime: 0,
    gcTime: 0,
    queryFn: () => fetchFullSsns(maskedEmployee!.id, "enrollment_form"),
  });
  const { employee, enrollmentContext } = useMemo(() => {
    if (!maskedEmployee || !fullSsns) {
      return { employee: maskedEmployee, enrollmentContext: maskedEnrollmentContext };
    }
    const deps = Array.isArray(maskedEnrollmentContext?.dependents) ? maskedEnrollmentContext.dependents : [];
    const merged = applyFullSsns(maskedEmployee, deps, fullSsns);
    return {
      employee: merged.employee,
      enrollmentContext: maskedEnrollmentContext
        ? { ...maskedEnrollmentContext, dependents: merged.dependents }
        : maskedEnrollmentContext,
    };
  }, [maskedEmployee, maskedEnrollmentContext, fullSsns]);
  // The impersonation banner is a 2.25rem (h-9) fixed bar at z-[60] that paints
  // over the top of this z-50 centered dialog, clipping the header/signature
  // area. When impersonating, recenter the dialog within the area below the
  // banner and clamp its height so nothing is hidden behind the bar.
  const isImpersonating = !!user?.isImpersonating;
  const BANNER_HEIGHT = "2.25rem";
  // Shift down AND shrink by the banner height so the box centers in the area
  // below the banner — offsetting alone (with a fixed h-[88vh]) pushed the
  // footer/Finish button off-screen. height overrides the className's h-[88vh].
  const dialogStyle = isImpersonating
    ? ({
        top: `calc(50% + ${BANNER_HEIGHT} / 2)`,
        height: `calc(88vh - ${BANNER_HEIGHT})`,
        maxHeight: `calc(88vh - ${BANNER_HEIGHT})`,
      } as React.CSSProperties)
    : undefined;

  const [confirming, setConfirming] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  // Signed signature fields, keyed `${formId}:${planId}:${fieldId}`.
  const [signedKeys, setSignedKeys] = useState<Set<string>>(new Set());
  // Auto-stamped signature-date values, keyed assocKey -> { fieldId: dateText }.
  const [dateOverrides, setDateOverrides] = useState<Record<string, Record<number, string>>>({});
  // Forms whose PDF failed to load (missing/corrupt file) — excluded from the gate.
  const [failedFormKeys, setFailedFormKeys] = useState<Set<string>>(new Set());
  const viewerRef = useRef<SigningDocViewerHandle>(null);

  // Read the latest draft-restored keys / change handler without making them
  // effect deps (which would reseed mid-session and wipe in-progress signs).
  const initialSignedKeysRef = useRef(initialSignedKeys);
  initialSignedKeysRef.current = initialSignedKeys;
  const onSignedKeysChangeRef = useRef(onSignedKeysChange);
  onSignedKeysChangeRef.current = onSignedKeysChange;

  useEffect(() => {
    if (open) {
      setActiveIndex(0);
      // Resume any signing progress saved to the enrollment draft.
      setSignedKeys(new Set(initialSignedKeysRef.current ?? []));
      setDateOverrides({});
      setFailedFormKeys(new Set());
    }
  }, [open, selectedPlanIds.join(",")]);

  // Report signed-key changes up so the parent can autosave signing progress.
  useEffect(() => {
    if (!open) return;
    onSignedKeysChangeRef.current?.(Array.from(signedKeys));
  }, [open, signedKeys]);

  const { data: batchData, isLoading: batchLoading } = useQuery({
    queryKey: ["plan-forms-signing-batch", selectedPlanIds],
    enabled: open && selectedPlanIds.length > 0,
    queryFn: async () => {
      const res = await fetch("/api/plans/forms/batch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ planIds: selectedPlanIds }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error || err?.message || "Failed to fetch forms");
      }
      return res.json();
    },
  });

  const planFormsData = useMemo(() => {
    const associations = Array.isArray(batchData?.associations)
      ? (batchData.associations as any[])
      : [];
    return associations.filter((assoc: any) => assoc?.isRequired !== false);
  }, [batchData]);

  const formMappings = useMemo(() => {
    const mappings = batchData?.mappingsByFormId;
    return mappings && typeof mappings === "object" ? mappings : {};
  }, [batchData]);

  const planById = useMemo(() => {
    const fromContext = enrollmentContext?.planById;
    if (fromContext && typeof fromContext === "object") return fromContext;
    return {} as Record<number, any>;
  }, [enrollmentContext]);

  // ---- Mapping context (ported from FormReviewModal) -----------------------
  const resolveSelectedDependents = (planId: number) => {
    const selections = enrollmentContext?.selectedModifyPlans || {};
    const selected = Object.values(selections).find(
      (sel: any) => Number(sel?.planId) === Number(planId)
    ) as any;
    const dependentIds = Array.isArray(selected?.dependentIds) ? selected.dependentIds : [];
    const allDependents = Array.isArray(enrollmentContext?.dependents)
      ? enrollmentContext.dependents
      : [];
    const selectedDependents = allDependents.filter((dep: any) =>
      dependentIds.includes(dep.id)
    );
    return {
      dependentIds,
      dependents: selectedDependents,
      primaryDependent: selectedDependents[0] || null,
    };
  };

  const computeCoverageTier = (deps: any[]): string => {
    const spouseRels = ["spouse", "domestic_partner", "domestic partner"];
    const hasSpouse = deps.some((d: any) =>
      spouseRels.includes(String(d.relationship).toLowerCase())
    );
    const hasChild = deps.some((d: any) =>
      String(d.relationship).toLowerCase().startsWith("child")
    );
    if (hasSpouse && hasChild) return "family";
    if (hasSpouse) return "employee_spouse";
    if (hasChild) return "employee_children";
    return "employee_only";
  };

  const buildMappingContext = (planId: number) => {
    const selected = resolveSelectedDependents(planId);
    const coverageTier = computeCoverageTier(selected.dependents);
    const effectiveDate = enrollmentContext?.qleEffectiveDate
      ? new Date(enrollmentContext.qleEffectiveDate).toISOString()
      : undefined;

    const allDependents = Array.isArray(enrollmentContext?.dependents)
      ? enrollmentContext.dependents
      : [];
    const allChildren = allDependents
      .filter((d: any) => String(d?.relationship ?? "").toLowerCase().startsWith("child"))
      .slice()
      .sort((a: any, b: any) => Number(a.id) - Number(b.id));
    const spouseRels = ["spouse", "domestic_partner", "domestic partner"];
    const selectedModifyPlans = enrollmentContext?.selectedModifyPlans || {};
    const waivedCoverageByType = enrollmentContext?.waivedCoverageByType || {};

    const computeFlagsForType = (type: string) => {
      const sel = selectedModifyPlans[type] as any;
      const isWaived = !!waivedCoverageByType[type];
      const depIds: number[] = Array.isArray(sel?.dependentIds)
        ? sel.dependentIds.map((id: any) => Number(id))
        : [];
      const depIdSet = new Set(depIds);
      const isCovered = !!sel?.planId && !isWaived;
      const coveredSpouse =
        isCovered &&
        allDependents.some(
          (d: any) =>
            spouseRels.includes(String(d?.relationship ?? "").toLowerCase()) &&
            depIdSet.has(Number(d.id))
        );
      const dependentCovered = (idx: number) =>
        isCovered && !!allChildren[idx] && depIdSet.has(Number(allChildren[idx].id)) ? "X" : "";
      const anyChild = [0, 1, 2, 3, 4].some((i) => dependentCovered(i) === "X");
      return {
        coverageEmployee: isCovered ? "X" : "",
        coverageSpouse: coveredSpouse ? "X" : "",
        coverageChild: anyChild ? "X" : "",
        coverageDependent1: dependentCovered(0),
        coverageDependent2: dependentCovered(1),
        coverageDependent3: dependentCovered(2),
        coverageDependent4: dependentCovered(3),
        coverageDependent5: dependentCovered(4),
        coverageDecline: isCovered ? "" : "X",
      };
    };

    const buildPlanInfo = (plan: any) => {
      if (!plan) {
        return {
          id: "", systemPlanId: "", planName: "", planCheckbox: "", planCode: "",
          adminName: "", networkType: "", metalTier: "", description: "",
          taxDetermination: "", waitingPeriod: "", subGroupNumber: "",
          networkHmo: "", networkPpo: "", networkHsa: "", networkEpo: "",
        };
      }
      const networkType = String(plan.networkType ?? "").toUpperCase();
      return {
        id: plan.id ?? "",
        systemPlanId: plan.systemPlanId ?? "",
        planName: plan.name ?? "",
        planCheckbox: "X",
        planCode: plan.planCode ?? "",
        adminName: plan.adminName ?? "",
        networkType: plan.networkType ?? "",
        metalTier: plan.metalTier ?? "",
        description: plan.description ?? "",
        taxDetermination: plan.taxDetermination ?? "",
        waitingPeriod: plan.waitingPeriod ?? "",
        subGroupNumber: plan.subGroupNumber ?? "",
        networkHmo: networkType === "HMO" ? "X" : "",
        networkPpo: networkType === "PPO" ? "X" : "",
        networkHsa: networkType === "HSA" || networkType === "HDHP" ? "X" : "",
        networkEpo: networkType === "EPO" ? "X" : "",
      };
    };

    const planTypeAliases: Array<[string[], string]> = [
      [["health", "medical"], "medicalPlan"],
      [["dental"], "dentalPlan"],
      [["vision"], "visionPlan"],
      [["life"], "lifePlan"],
    ];
    const planContext: Record<string, any> = {};
    for (const [aliases, key] of planTypeAliases) {
      const active =
        aliases.find((t) => selectedModifyPlans[t]?.planId) ||
        aliases.find((t) => waivedCoverageByType[t]) ||
        aliases[0];
      const sel = selectedModifyPlans[active] as any;
      const planForType = sel?.planId ? planById?.[sel.planId] : null;
      planContext[key] = { ...buildPlanInfo(planForType), ...computeFlagsForType(active) };
    }

    const buildDependentInfo = (dep: any) => {
      if (!dep) return {};
      return { ...dep, ...deriveSourceFields("dependent1", dep) };
    };
    const spouseRecord = allDependents.find((d: any) =>
      spouseRels.includes(String(d?.relationship ?? "").toLowerCase())
    );
    const spouseInfo = spouseRecord
      ? { ...spouseRecord, ...deriveSourceFields("spouse", spouseRecord) }
      : {};
    const dependentInfoByIndex: Record<string, any> = {
      dependent1: buildDependentInfo(allChildren[0]),
      dependent2: buildDependentInfo(allChildren[1]),
      dependent3: buildDependentInfo(allChildren[2]),
      dependent4: buildDependentInfo(allChildren[3]),
      dependent5: buildDependentInfo(allChildren[4]),
    };

    return {
      employee: employee
        ? { ...employee, ...deriveSourceFields("employee", employee) }
        : undefined,
      enrollment: { planId, dependentIds: selected.dependentIds, coverageTier, effectiveDate },
      plan: planById?.[planId],
      dependents: selected.dependents,
      dependent: selected.primaryDependent,
      signature: { signedContent: signatureData || "" },
      spouse: spouseInfo,
      ...dependentInfoByIndex,
      ...planContext,
    } as any;
  };

  // Full populated values per association, keyed by formFieldId (incl. signatures).
  const fieldValuesByAssociation = useMemo(() => {
    const values: Record<string, Record<number, any>> = {};
    for (const assoc of planFormsData as any[]) {
      const formId = assoc?.form?.id ?? assoc?.formId ?? assoc?.id;
      const planId = Number(assoc?.planId);
      if (!formId || !planId) continue;
      const mappings = (formMappings[formId] || []) as FieldMapping[];
      const context = buildMappingContext(planId);
      values[`${formId}:${planId}`] = populateFormFields(mappings, context);
    }
    return values;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planFormsData, formMappings, employee, enrollmentContext, signatureData, planById]);

  // ---- Per-form descriptors -----------------------------------------------
  const forms = useMemo(() => {
    return (planFormsData as any[]).map((assoc) => {
      const formId = assoc?.form?.id ?? assoc?.formId ?? assoc?.id;
      const planId = Number(assoc?.planId);
      const documentId = assoc?.form?.documentId;
      const carrierFormId = assoc?.form?.carrierFormId;
      const pdfUrl = carrierFormId
        ? `/api/carrier-forms/${carrierFormId}/content`
        : undefined;
      const fields = (assoc?.form?.fields ?? []) as SigningField[];
      const signatureFields = fields.filter(isSignatureType);
      const signatureFieldIds = signatureFields.map((f) => f.id);
      // Only signature fields with a positioned box can be signed on the doc.
      const placeableSignatureFieldIds = signatureFields
        .filter(hasPlacedCoords)
        .map((f) => f.id);
      const hasSignatures = signatureFieldIds.length > 0;
      // "Not ready to sign": the form needs signatures but none are positioned.
      const signReady = !hasSignatures || placeableSignatureFieldIds.length > 0;
      // Carrier form that failed to prepare (analysis failed / no fields). Such
      // a form can't request its required signature, so it must not be silently
      // completed.
      const notReady = isFormNotReady(assoc, fields.length);
      return {
        assoc,
        formId,
        planId,
        documentId,
        pdfUrl,
        formName: assoc?.form?.name ?? assoc?.name ?? "Form",
        fields,
        signatureFieldIds,
        placeableSignatureFieldIds,
        hasSignatures,
        signReady,
        notReady,
        assocKey: `${formId}:${planId}`,
        hasDoc: Boolean(documentId || pdfUrl),
      };
    });
  }, [planFormsData]);

  // Signature-date field ids per association (auto-stamped on sign).
  const signatureDateFieldIdsByAssoc = useMemo(() => {
    const out: Record<string, number[]> = {};
    for (const f of forms) {
      const mappings = (formMappings[f.formId] || []) as FieldMapping[];
      out[f.assocKey] = mappings
        .filter((m) => /signaturedate/i.test(String(m.sourcePath ?? "")))
        .map((m) => m.formFieldId);
    }
    return out;
  }, [forms, formMappings]);

  const sigKey = (formId: number, planId: number, fieldId: number) =>
    `${formId}:${planId}:${fieldId}`;

  // All required signature keys: only forms with a document AND signature
  // fields that are actually positioned on the page (placeable). Unplaced
  // forms can't be signed here, so they don't block completion (guard).
  const requiredSigKeys = useMemo(() => {
    const keys: string[] = [];
    for (const f of forms) {
      if (!f.hasDoc) continue;
      if (failedFormKeys.has(f.assocKey)) continue; // document couldn't load
      // Not-ready forms can have leftover placed fields (e.g. analysis_failed
      // after a partial analysis) but render an info panel instead of the doc,
      // so their signatures can't be collected — they're pending, not required.
      if (f.notReady) continue;
      for (const fieldId of f.placeableSignatureFieldIds) {
        keys.push(sigKey(f.formId, f.planId, fieldId));
      }
    }
    return keys;
  }, [forms, failedFormKeys]);

  const allSigned = requiredSigKeys.length > 0
    ? requiredSigKeys.every((k) => signedKeys.has(k))
    : true;
  const signedCount = requiredSigKeys.filter((k) => signedKeys.has(k)).length;

  // Carrier forms that failed to prepare can't be signed here. They don't
  // block completion: they're surfaced in the UI and reported as pendingForms
  // on finish so the admin can fix the form and collect the signature later.
  const notReadyForms = useMemo(() => forms.filter((f) => f.notReady), [forms]);
  const hasNotReadyForms = notReadyForms.length > 0;

  const signedCountForForm = (f: (typeof forms)[number]) =>
    f.placeableSignatureFieldIds.filter((id) => signedKeys.has(sigKey(f.formId, f.planId, id))).length;

  const activeForm = forms[activeIndex];

  // Sign a field: record it + auto-stamp this form's signature-date fields.
  const handleSignField = (fieldId: number) => {
    if (!activeForm) return;
    const key = sigKey(activeForm.formId, activeForm.planId, fieldId);
    setSignedKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    const dateIds = signatureDateFieldIdsByAssoc[activeForm.assocKey] || [];
    if (dateIds.length) {
      setDateOverrides((prev) => {
        const forAssoc = { ...(prev[activeForm.assocKey] || {}) };
        const stamp = todayLabel();
        for (const id of dateIds) forAssoc[id] = stamp;
        return { ...prev, [activeForm.assocKey]: forAssoc };
      });
    }
  };

  // Track per-form document load success/failure (failures leave the gate).
  const handleFormLoadError = () => {
    if (!activeForm) return;
    const key = activeForm.assocKey;
    setFailedFormKeys((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };
  const handleFormLoaded = () => {
    if (!activeForm) return;
    const key = activeForm.assocKey;
    setFailedFormKeys((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  // Values for the active form's viewer: data fields only (signatures excluded),
  // with auto-stamped dates applied.
  const activeDataValueById = useMemo(() => {
    if (!activeForm) return {};
    const full = fieldValuesByAssociation[activeForm.assocKey] || {};
    const sigIds = new Set(activeForm.signatureFieldIds);
    const result: Record<number, string> = {};
    for (const [idStr, val] of Object.entries(full)) {
      const id = Number(idStr);
      if (sigIds.has(id)) continue; // signatures placed manually
      if (val !== null && val !== undefined) result[id] = String(val);
    }
    const overrides = dateOverrides[activeForm.assocKey] || {};
    for (const [idStr, val] of Object.entries(overrides)) result[Number(idStr)] = val;
    return result;
  }, [activeForm, fieldValuesByAssociation, dateOverrides]);

  const activeSignedFieldIds = useMemo(() => {
    if (!activeForm) return new Set<number>();
    return new Set(
      activeForm.signatureFieldIds.filter((id) =>
        signedKeys.has(sigKey(activeForm.formId, activeForm.planId, id))
      )
    );
  }, [activeForm, signedKeys]);

  // "Next": jump to the next unsigned signature field (switching forms if needed).
  const handleNext = () => {
    for (let i = 0; i < forms.length; i++) {
      const f = forms[i];
      if (!f.hasDoc || f.notReady || failedFormKeys.has(f.assocKey)) continue;
      const nextUnsigned = f.placeableSignatureFieldIds.find(
        (id) => !signedKeys.has(sigKey(f.formId, f.planId, id))
      );
      if (nextUnsigned !== undefined) {
        if (i !== activeIndex) {
          setActiveIndex(i);
          // Allow the viewer to mount/render before scrolling.
          setTimeout(() => viewerRef.current?.scrollToField(nextUnsigned), 300);
        } else {
          viewerRef.current?.scrollToField(nextUnsigned);
        }
        return;
      }
    }
  };

  // Build the final value map for a form: full mapped values + auto-stamped
  // dates + the signature image explicitly placed on every signed signature
  // field (so even signature fields without a data mapping get drawn).
  const finalValueMapFor = (f: (typeof forms)[number]) => {
    const map: Record<number, any> = {
      ...(fieldValuesByAssociation[f.assocKey] || {}),
      ...(dateOverrides[f.assocKey] || {}),
    };
    for (const fieldId of f.signatureFieldIds) {
      if (signedKeys.has(sigKey(f.formId, f.planId, fieldId))) {
        map[fieldId] = signatureData;
      }
    }
    return map;
  };

  const handleFinish = async () => {
    try {
      setConfirming(true);

      const generatedPdfs: Array<{ formId: number; planId: number; formName: string; pdfBytes: Uint8Array }> = [];
      for (const f of forms) {
        // Not-ready forms can't be filled/signed — recorded as pending instead.
        if (!f.formId || !f.planId || !f.hasDoc || f.notReady) continue;
        try {
          const pdfBytes = await generateFilledPdf({
            documentId: f.documentId,
            pdfContentUrl: f.pdfUrl,
            fields: f.fields as any[],
            fieldValues: finalValueMapFor(f),
          });
          generatedPdfs.push({ formId: f.formId, planId: f.planId, formName: f.formName, pdfBytes });
        } catch (err) {
          console.error(`[EnrollmentSigningView] Failed to generate PDF for form ${f.formId}:`, err);
        }
      }

      const formData = {
        signatureName,
        signatureData,
        generatedPdfs,
        // Forms whose signature couldn't be collected (failed carrier-form
        // prep) — recorded in signedContent so the admin can follow up.
        pendingForms: notReadyForms.map((f) => ({
          formId: f.formId,
          carrierFormId: f.assoc?.form?.carrierFormId ?? null,
          planId: f.planId,
          formName: f.formName,
        })),
        forms: forms.filter((f) => !f.notReady).map((f) => {
          const finalMap = finalValueMapFor(f);
          return {
            formId: f.formId,
            planId: f.planId,
            fieldData: ((formMappings[f.formId] || []) as FieldMapping[]).map((m) => {
              const v = finalMap[m.formFieldId];
              return {
                fieldId: m.formFieldId,
                mappingId: m.id,
                value: v === null || v === undefined ? "" : String(v),
              };
            }),
          };
        }),
      };
      await onConfirm(formData);
    } catch (error) {
      // onConfirm (submitEnrollment) surfaces its own success/error toast; here
      // we just stop the loader so the modal stays usable for a retry.
      console.error("[EnrollmentSigningView] enrollment submit failed:", error);
    } finally {
      setConfirming(false);
    }
  };

  const busy = confirming || isLoading;
  const isDataLoading = batchLoading || ssnLoading;
  const handleBack = onBack ?? (() => onOpenChange(false));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        style={dialogStyle}
        // No `relative` here: tailwind-merge treats it as conflicting with the
        // base DialogContent `fixed` and drops `fixed`, breaking viewport
        // centering (modal renders half off-screen). `fixed` already anchors
        // the absolute "Submitting…" overlay.
        className="flex h-[88vh] max-h-[88vh] w-[95vw] max-w-6xl flex-col gap-0 overflow-hidden p-0"
      >
        {confirming && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-white/85 backdrop-blur-sm">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm font-medium text-muted-foreground">
              Submitting your enrollment…
            </p>
          </div>
        )}
        <DialogHeader className="border-b px-4 py-3">
          <DialogTitle>Review &amp; sign your enrollment</DialogTitle>
        </DialogHeader>

        {isDataLoading ? (
          <div className="flex flex-1 items-center justify-center text-gray-500">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : forms.length === 0 ? (
          <div className="flex flex-1 items-center justify-center px-6 text-center">
            <p className="text-sm text-gray-600">
              No forms are associated with your selected benefits. Click Finish to complete.
            </p>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1">
            {/* Left: table of contents */}
            <aside className="w-60 shrink-0 overflow-y-auto border-r bg-gray-50 p-3">
              <h3 className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Forms
              </h3>
              <ul className="space-y-1">
                {forms.map((f, i) => {
                  const failed = failedFormKeys.has(f.assocKey);
                  const total = f.placeableSignatureFieldIds.length;
                  const done = signedCountForForm(f);
                  const complete = !failed && !f.notReady && (total === 0 || done === total);
                  return (
                    <li key={`${f.assocKey}-${i}`}>
                      <button
                        type="button"
                        onClick={() => setActiveIndex(i)}
                        className={
                          "flex w-full items-start gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors " +
                          (i === activeIndex ? "bg-blue-100 text-blue-900" : "hover:bg-gray-100")
                        }
                      >
                        <span className="mt-0.5">
                          {f.notReady ? (
                            <AlertTriangle className="h-4 w-4 text-amber-500" />
                          ) : failed ? (
                            <AlertTriangle className="h-4 w-4 text-amber-500" />
                          ) : complete ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <FileText className="h-4 w-4 text-gray-400" />
                          )}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">{f.formName}</span>
                          <span
                            className={
                              "text-xs " +
                              (f.notReady ? "text-amber-600" : "text-muted-foreground")
                            }
                          >
                            {f.notReady
                              ? "Signature pending — admin will follow up"
                              : failed
                              ? "Couldn't load this form"
                              : total === 0
                              ? "No signature needed"
                              : `${done}/${total} signed`}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>

            {/* Right: active form document */}
            <main className="flex min-w-0 flex-1 flex-col">
              <div className="flex items-center justify-between border-b px-4 py-2">
                <span className="truncate text-sm font-medium">{activeForm?.formName}</span>
                <Button size="sm" onClick={handleNext} disabled={allSigned}>
                  Next <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
              <div className="min-h-0 flex-1">
                {activeForm?.notReady ? (
                  <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
                    <AlertTriangle className="h-8 w-8 text-amber-500" />
                    <p className="text-sm font-medium text-amber-700">
                      This form isn’t ready to sign
                    </p>
                    <p className="max-w-md text-sm text-gray-600">
                      “{activeForm.formName}” couldn’t be prepared for signing, so its
                      signature can’t be collected here. You can still finish
                      enrollment — your administrator will be notified and will
                      follow up to collect it.
                    </p>
                  </div>
                ) : activeForm?.pdfUrl ? (
                  <SigningDocViewer
                    ref={viewerRef}
                    key={activeForm.assocKey}
                    pdfUrl={activeForm.pdfUrl}
                    fields={activeForm.fields}
                    dataValueById={activeDataValueById}
                    signedFieldIds={activeSignedFieldIds}
                    signatureData={signatureData}
                    onSignField={handleSignField}
                    onLoadError={handleFormLoadError}
                    onLoaded={handleFormLoaded}
                  />
                ) : (
                  <div className="flex h-full items-center justify-center px-6 text-center text-sm text-gray-500">
                    This form has no document to preview. Your selections are recorded; click Finish to
                    complete.
                  </div>
                )}
              </div>
            </main>
          </div>
        )}

        {hasNotReadyForms && (
          <div className="flex items-start gap-2 border-t border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {notReadyForms.length === 1 ? "A form" : `${notReadyForms.length} forms`}{" "}
              couldn’t be prepared for signing. You can still finish enrollment —
              your administrator will be notified and will follow up to collect{" "}
              {notReadyForms.length === 1 ? "this signature" : "these signatures"}.
            </span>
          </div>
        )}
        <DialogFooter className="border-t px-4 py-3 sm:justify-between">
          <Button variant="outline" onClick={handleBack} disabled={busy}>
            <ArrowLeft className="mr-1 h-4 w-4" /> Back to elections
          </Button>
          <div className="flex items-center gap-3">
            {requiredSigKeys.length > 0 && (
              <span className="text-sm text-muted-foreground">
                {signedCount} of {requiredSigKeys.length} signed
              </span>
            )}
            <Button
              onClick={handleFinish}
              disabled={busy || !allSigned}
            >
              {confirming ? "Completing..." : "Finish"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
